import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PortalPageHeading from "../components/portal/PortalPageHeading";
import PortalProgressCard from "../components/portal/PortalProgressCard";
import PortalVisitHistory from "../components/portal/PortalVisitHistory";
import { usePortalPage } from "../features/portal/hooks/usePortalPage";
import { selectPortalVisits } from "../features/portal/portal-slice";
import { fetchPortalVisits } from "../features/portal/slice-extensions/portal-slice-extension";

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

/** Progress: where the member stands in their training, and the visits of the last three months. */
const PortalProgressPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { session, memberId, members, selectMember } = usePortalPage();
  const visits = useAppSelector(selectPortalVisits(memberId ?? ""));

  const range = useMemo(() => {
    const threeMonthsBack = new Date();
    threeMonthsBack.setDate(threeMonthsBack.getDate() - 90);
    return { from: toIsoDate(threeMonthsBack), to: toIsoDate(new Date()) };
  }, []);

  useEffect(() => {
    if (memberId) dispatch(fetchPortalVisits({ memberId, from: range.from, to: range.to }));
  }, [memberId, range, dispatch]);

  if (!session) return null;

  return (
    <section className="mx-auto w-full max-w-3xl px-4 py-8">
      <PortalPageHeading
        title={t("portal.progress.title")}
        intro={t("portal.progress.intro")}
        members={members}
        memberId={memberId}
        onSelectMember={selectMember}
      />
      {memberId && <PortalProgressCard memberId={memberId} />}
      <h2 className="mb-3 text-lg font-semibold text-grey-20 dark:text-white">{t("portal.visitHistory")}</h2>
      <PortalVisitHistory visits={visits} />
    </section>
  );
};

export default PortalProgressPage;
